import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';
import { useLanguages } from '../context/LanguagesContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  SealCheck,
  User as UserIcon,
  IdentificationBadge,
  ChartLine,
  Lock,
  Gear,
} from '@phosphor-icons/react';
import { PhotoCropModal } from '../components/profile/PhotoCropModal';
import { PhoneVerifyModal } from '../components/profile/PhoneVerifyModal';
import { ActivityStats } from '../components/profile/ActivityStats';
import { AccountManagementSection } from '../components/profile/AccountManagementSection';
import { PersonalTab } from '../components/profile/PersonalTab';
import { PublicTab } from '../components/profile/PublicTab';
import { SecurityTab } from '../components/profile/SecurityTab';
import { VerificationTab } from '../components/profile/VerificationTab';
import { isEmailNotVerifiedError, showEmailVerificationRequiredToast } from '../utils/emailVerification';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BIO_MAX = 300;

export const ProfileSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser, loading: authLoading } = useAuth();
  const { items: languages } = useLanguages();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [bio, setBio] = useState('');
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);
  const [facebookUrl, setFacebookUrl] = useState('');
  const [sellerType, setSellerType] = useState('private');
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [businessNif, setBusinessNif] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('fr');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [photoSrc, setPhotoSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [phoneOpen, setPhoneOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);
  const [verification, setVerification] = useState(null);
  const [activeCategory, setActiveCategory] = useState('personal');

  const [regionsData, setRegionsData] = useState({ regions: [], cities_by_region: {} });
  const [allCities, setAllCities] = useState([]);

  const citiesForRegion = region && regionsData.cities_by_region?.[region]
    ? [...regionsData.cities_by_region[region]].sort()
    : allCities;

  const fetchProfile = useCallback(async () => {
    try {
      const [profileRes, verificationRes, regionsRes, citiesRes] = await Promise.all([
        axios.get(`${API}/auth/profile-full`, { withCredentials: true }),
        axios.get(`${API}/users/me/verification`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/regions`).catch(() => null),
        axios.get(`${API}/cities`).catch(() => null),
      ]);
      const p = profileRes.data;
      setProfile(p);
      setName(p.name || '');
      setDisplayName(p.display_name || '');
      setCity(p.city || '');
      setRegion(p.region || '');
      setBio(p.bio || '');
      setWhatsappEnabled(!!p.whatsapp_enabled);
      setFacebookUrl(p.facebook_url || '');
      setSellerType(p.seller_type === 'business' ? 'business' : 'private');
      setBusinessName(p.business_name || '');
      setBusinessAddress(p.business_address || '');
      setBusinessNif(p.business_nif || '');
      setPreferredLanguage(p.preferred_language || 'fr');
      if (verificationRes?.data) setVerification(verificationRes.data);
      if (regionsRes?.data) setRegionsData(regionsRes.data);
      if (citiesRes?.data) setAllCities(citiesRes.data);
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for AuthContext to finish resolving /auth/me before deciding
    // whether the visitor is authenticated. On hard-reload of /settings the
    // user object is briefly null while the cookie is still valid — bouncing
    // to /login here would be a UX regression.
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchProfile();
  }, [authLoading, user, navigate, fetchProfile]);

  const handleRegionChange = (val) => {
    setRegion(val);
    setCity('');
  };

  const hasVerifiedPhone = !!profile?.phone && !!profile?.phone_verified;

  const trimmedBusinessName = (businessName || '').trim();
  const businessNameRequired = sellerType === 'business';
  const businessNameMissing = businessNameRequired && trimmedBusinessName.length === 0;

  const isValidFacebookUrl = (u) => {
    if (!u || !u.trim()) return true;
    // MUST stay in sync with backend FACEBOOK_URL_REGEX in routes/auth.py
    return /^https:\/\/(www\.|m\.|mobile\.|web\.)?(facebook\.com|fb\.com)\/[A-Za-z0-9._-]+/i.test(u.trim());
  };

  const handleSave = async () => {
    if (bio.length > BIO_MAX) {
      toast.error(t('settings.bioCounter', { count: bio.length }));
      return;
    }
    if (whatsappEnabled && !hasVerifiedPhone) {
      toast.error(t('settings.whatsappRequiresPhone'));
      return;
    }
    if (businessNameMissing) {
      toast.error(t('settings.businessNameRequired'));
      return;
    }
    if (facebookUrl && !isValidFacebookUrl(facebookUrl)) {
      toast.error(t('profileSettings.public.facebookInvalid'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/profile`,
        {
          name,
          display_name: displayName,
          city,
          region: region || null,
          bio: bio || null,
          whatsapp_enabled: whatsappEnabled,
          facebook_url: facebookUrl || null,
          seller_type: sellerType,
          business_name: sellerType === 'business' ? trimmedBusinessName : null,
          business_address: sellerType === 'business' ? (businessAddress || null) : null,
          business_nif: sellerType === 'business' ? (businessNif || null) : null,
          preferred_language: preferredLanguage,
        },
        { withCredentials: true },
      );
      toast.success(t('settings.saved'));
      if (refreshUser) refreshUser();
      fetchProfile();
    } catch (error) {
      if (isEmailNotVerifiedError(error)) {
        showEmailVerificationRequiredToast(t);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('JPG, PNG or WEBP only'); return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoSrc(reader.result);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropConfirm = async (blob) => {
    setUploadingPhoto(true);
    try {
      const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
      const formData = new FormData();
      formData.append('file', new File([blob], `avatar.${ext}`, { type: blob.type || 'image/webp' }));
      await axios.post(`${API}/auth/profile-photo`, formData, { withCredentials: true });
      toast.success(t('settings.photoUpdated'));
      setCropOpen(false);
      setPhotoSrc(null);
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t('auth.passwordMismatch')); return; }
    if (newPassword.length < 8) { toast.error(t('settings.minPassword')); return; }
    setChangingPw(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      toast.success(t('settings.passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  const getAvatarUrl = (picturePath) => {
    if (!picturePath) return null;
    if (picturePath.startsWith('http')) return picturePath;
    return `${API}/files/${picturePath}`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Header />
        <div className="flex justify-center py-20">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const categories = [
    { key: 'personal',     icon: UserIcon,               labelKey: 'profileSettings.categories.personal',     danger: false },
    { key: 'public',       icon: IdentificationBadge,    labelKey: 'profileSettings.categories.public',       danger: false },
    { key: 'activity',     icon: ChartLine,              labelKey: 'profileSettings.categories.activity',     danger: false },
    { key: 'security',     icon: Lock,                   labelKey: 'profileSettings.categories.security',     danger: false },
    { key: 'verification', icon: SealCheck,              labelKey: 'profileSettings.categories.verification', danger: false },
    { key: 'account',      icon: Gear,                   labelKey: 'profileSettings.categories.account',      danger: true },
  ];

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-4 md:mb-6" data-testid="settings-title">
          {t('profileSettings.title') || t('nav.profileSettings')}
        </h1>

        {/* Mobile/tablet: horizontal scrollable tab bar. Hidden on md+. */}
        <div className="md:hidden -mx-4 px-4 mb-4 overflow-x-auto" data-testid="profile-sidebar-mobile">
          <div className="flex gap-2 min-w-max">
            {categories.map((c) => {
              const active = activeCategory === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setActiveCategory(c.key)}
                  className={`whitespace-nowrap px-3 py-2 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${
                    active
                      ? c.danger
                        ? 'bg-danger text-white border-danger'
                        : 'bg-primary text-white border-primary'
                      : c.danger
                      ? 'bg-danger-soft text-danger border-danger/30'
                      : 'bg-white text-text_main border-border'
                  }`}
                  data-testid={`profile-tab-${c.key}`}
                >
                  <c.icon size={14} weight={active ? 'fill' : 'regular'} />
                  {t(c.labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="md:grid md:grid-cols-[220px_1fr] md:gap-6">
          {/* Desktop sidebar */}
          <aside className="hidden md:block" data-testid="profile-sidebar">
            <nav className="bg-white border border-border rounded-2xl p-2 sticky top-24">
              {categories.filter((c) => !c.danger).map((c) => {
                const active = activeCategory === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveCategory(c.key)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-primary/5 text-primary font-semibold' : 'hover:bg-surface_muted text-text_main'
                    }`}
                    data-testid={`profile-sidebar-${c.key}`}
                  >
                    <c.icon size={16} weight={active ? 'fill' : 'regular'} />
                    {t(c.labelKey)}
                  </button>
                );
              })}
              <hr className="my-2 border-border" />
              {categories.filter((c) => c.danger).map((c) => {
                const active = activeCategory === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveCategory(c.key)}
                    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-danger-soft text-danger font-semibold' : 'text-danger hover:bg-danger-soft/50'
                    }`}
                    data-testid={`profile-sidebar-${c.key}`}
                  >
                    <c.icon size={16} weight={active ? 'fill' : 'regular'} />
                    {t(c.labelKey)}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content column */}
          <div className="space-y-6">
            {activeCategory === 'personal' && (
              <PersonalTab
                name={name} setName={setName}
                profile={profile}
                region={region} city={city}
                regionsData={regionsData} citiesForRegion={citiesForRegion} allCities={allCities}
                onRegionChange={handleRegionChange} setCity={setCity}
                preferredLanguage={preferredLanguage} setPreferredLanguage={setPreferredLanguage}
                languages={languages}
                hasVerifiedPhone={hasVerifiedPhone}
                onOpenPhoneModal={() => setPhoneOpen(true)}
                onSave={handleSave} saving={saving} bio={bio} bioMax={BIO_MAX}
                t={t}
              />
            )}

            {activeCategory === 'public' && (
              <PublicTab
                profile={profile}
                fileRef={fileRef}
                onPhotoSelect={handlePhotoSelect}
                onOpenFileDialog={() => fileRef.current?.click()}
                getAvatarUrl={getAvatarUrl}
                displayName={displayName} setDisplayName={setDisplayName}
                bio={bio} setBio={setBio} bioMax={BIO_MAX}
                facebookUrl={facebookUrl} setFacebookUrl={setFacebookUrl}
                isValidFacebookUrl={isValidFacebookUrl}
                whatsappEnabled={whatsappEnabled} setWhatsappEnabled={setWhatsappEnabled}
                hasVerifiedPhone={hasVerifiedPhone}
                sellerType={sellerType} setSellerType={setSellerType}
                businessName={businessName} setBusinessName={setBusinessName}
                businessNameMissing={businessNameMissing}
                businessAddress={businessAddress} setBusinessAddress={setBusinessAddress}
                businessNif={businessNif} setBusinessNif={setBusinessNif}
                onSave={handleSave} saving={saving}
                language={i18n.language}
                t={t}
              />
            )}

            {activeCategory === 'activity' && <ActivityStats />}

            {activeCategory === 'security' && (
              <SecurityTab
                currentPassword={currentPassword} setCurrentPassword={setCurrentPassword}
                newPassword={newPassword} setNewPassword={setNewPassword}
                confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword}
                changingPw={changingPw} onChangePassword={handleChangePassword}
                profile={profile}
                language={i18n.language}
                t={t}
              />
            )}

            {activeCategory === 'verification' && (
              <VerificationTab verification={verification} t={t} />
            )}

            {activeCategory === 'account' && <AccountManagementSection />}
          </div>
        </div>
      </div>

      <PhotoCropModal
        open={cropOpen}
        imageSrc={photoSrc}
        onClose={() => { setCropOpen(false); setPhotoSrc(null); }}
        onConfirm={handleCropConfirm}
        busy={uploadingPhoto}
      />
      <PhoneVerifyModal
        open={phoneOpen}
        currentPhone={profile?.phone || ''}
        onClose={() => setPhoneOpen(false)}
        onVerified={() => { fetchProfile(); if (refreshUser) refreshUser(); }}
      />

      <BottomNav />
    </div>
  );
};
